import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
  Legend,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import { 
  BookOpen, 
  CheckCircle, 
  Clock, 
  Target, 
  Award,
  Heart,
  BarChart3,
  BookX,
  ShoppingCart,
  TrendingUp,
  Calendar,
  DollarSign,
  Star,
  BookMarked,
  Users,
  Zap
} from 'lucide-react';
import { useAppState } from '../context/AppStateContext';

const MangaStatistics: React.FC = () => {
  const { state } = useAppState();

  const statistics = useMemo(() => {
    const colecciones = state.coleccionesManga;
    
    // Estadísticas básicas
    const totalColecciones = colecciones.length;
    const coleccionesCompletadas = colecciones.filter(c => c.isComplete).length;
    const coleccionesActivas = totalColecciones - coleccionesCompletadas;
    
    // Estadísticas de tomos
    const totalTomos = colecciones.reduce((sum, c) => sum + c.tomos.length, 0);
    const tomosLeidos = colecciones.reduce((sum, c) => sum + c.tomosLeidos, 0);
    const tomosComprados = colecciones.reduce((sum, c) => sum + c.tomosComprados, 0);
    const tomosTBR = colecciones.reduce((sum, c) => sum + c.tomos.filter(t => t.estado === 'tbr').length, 0);
    const tomosLeyendo = colecciones.reduce((sum, c) => sum + c.tomos.filter(t => t.estado === 'leyendo').length, 0);
    const tomosAbandonados = colecciones.reduce((sum, c) => sum + c.tomos.filter(t => t.estado === 'abandonado').length, 0);
    const tomosWishlist = colecciones.reduce((sum, c) => sum + c.tomos.filter(t => t.estado === 'wishlist').length, 0);
    
    // Valor total
    const valorTotal = colecciones.reduce((sum, c) => {
      if (c.precioPorTomo && c.precioPorTomo > 0) {
        const tomosReales = c.tomos.filter(t => 
          t.fechaCompra || 
          t.estado === 'comprado' || 
          t.estado === 'leido' || 
          t.estado === 'leyendo'
        ).length;
        return sum + (tomosReales * c.precioPorTomo);
      }
      return sum;
    }, 0);
    
    // Estadísticas por género
    const generos: Record<string, { total: number; completadas: number; tomos: number; tomosLeidos: number }> = {};
    
    colecciones.forEach(c => {
      if (c.genero) {
        if (!generos[c.genero]) {
          generos[c.genero] = { total: 0, completadas: 0, tomos: 0, tomosLeidos: 0 };
        }
        generos[c.genero].total++;
        generos[c.genero].tomos += c.tomos.length;
        generos[c.genero].tomosLeidos += c.tomosLeidos;
        if (c.isComplete) generos[c.genero].completadas++;
      }
    });
    
    // Estadísticas por editorial
    const editoriales: Record<string, { total: number; completadas: number; tomos: number; tomosLeidos: number }> = {};
    
    colecciones.forEach(c => {
      if (c.editorial) {
        if (!editoriales[c.editorial]) {
          editoriales[c.editorial] = { total: 0, completadas: 0, tomos: 0, tomosLeidos: 0 };
        }
        editoriales[c.editorial].total++;
        editoriales[c.editorial].tomos += c.tomos.length;
        editoriales[c.editorial].tomosLeidos += c.tomosLeidos;
        if (c.isComplete) editoriales[c.editorial].completadas++;
      }
    });
    
    // Estadísticas por tipo de tomo
    const tiposTomo: Record<string, { total: number; completadas: number; tomos: number; tomosLeidos: number }> = {};
    
    colecciones.forEach(c => {
      if (!tiposTomo[c.tipoTomo]) {
        tiposTomo[c.tipoTomo] = { total: 0, completadas: 0, tomos: 0, tomosLeidos: 0 };
      }
      tiposTomo[c.tipoTomo].total++;
      tiposTomo[c.tipoTomo].tomos += c.tomos.length;
      tiposTomo[c.tipoTomo].tomosLeidos += c.tomosLeidos;
      if (c.isComplete) tiposTomo[c.tipoTomo].completadas++;
    });
    
    // Estadísticas por idioma
    const idiomas: Record<string, { total: number; completadas: number; tomos: number; tomosLeidos: number }> = {};
    
    colecciones.forEach(c => {
      const idioma = c.idioma || 'Desconocido';
      if (!idiomas[idioma]) {
        idiomas[idioma] = { total: 0, completadas: 0, tomos: 0, tomosLeidos: 0 };
      }
      idiomas[idioma].total++;
      idiomas[idioma].tomos += c.tomos.length;
      idiomas[idioma].tomosLeidos += c.tomosLeidos;
      if (c.isComplete) idiomas[idioma].completadas++;
    });
    
    // Progreso de lectura por colección
    const progresoPorColeccion = colecciones.map(c => ({
      nombre: c.titulo,
      progreso: c.totalTomos > 0 ? (c.tomosLeidos / c.totalTomos) * 100 : 0,
      tomosLeidos: c.tomosLeidos,
      totalTomos: c.totalTomos,
      completada: c.isComplete
    })).sort((a, b) => b.progreso - a.progreso);
    
    // Estadísticas de compra por mes (últimos 12 meses)
    const meses = Array.from({ length: 12 }, (_, i) => {
      const fecha = new Date();
      fecha.setMonth(fecha.getMonth() - i);
      return fecha.toLocaleDateString('es-ES', { month: 'short', year: '2-digit' });
    }).reverse();
    
    const comprasPorMes = meses.map(mes => {
      const [mesStr, añoStr] = mes.split(' ');
      const año = parseInt('20' + añoStr);
      const mesNum = new Date(Date.parse(mesStr + ' 1, 2000')).getMonth();
      
      const tomosComprados = colecciones.reduce((sum, c) => {
        return sum + c.tomos.filter(t => {
          if (!t.fechaCompra) return false;
          const fechaCompra = new Date(t.fechaCompra);
          return fechaCompra.getFullYear() === año && fechaCompra.getMonth() === mesNum;
        }).length;
      }, 0);
      
      return { mes, tomos: tomosComprados };
    });
    
    // Estadísticas de lectura por mes
    const lecturasPorMes = meses.map(mes => {
      const [mesStr, añoStr] = mes.split(' ');
      const año = parseInt('20' + añoStr);
      const mesNum = new Date(Date.parse(mesStr + ' 1, 2000')).getMonth();
      
      const tomosLeidos = colecciones.reduce((sum, c) => {
        return sum + c.tomos.filter(t => {
          if (!t.fechaLecturaReciente) return false;
          const fechaLectura = new Date(t.fechaLecturaReciente);
          return fechaLectura.getFullYear() === año && fechaLectura.getMonth() === mesNum;
        }).length;
      }, 0);
      
      return { mes, tomos: tomosLeidos };
    });
    
    // Top colecciones por valor
    const topColeccionesValor = colecciones
      .filter(c => c.precioPorTomo && c.precioPorTomo > 0)
      .map(c => ({
        nombre: c.titulo,
        valor: c.tomos.filter(t => 
          t.fechaCompra || 
          t.estado === 'comprado' || 
          t.estado === 'leido' || 
          t.estado === 'leyendo'
        ).length * (c.precioPorTomo || 0),
        tomos: c.tomos.length,
        completada: c.isComplete
      }))
      .sort((a, b) => b.valor - a.valor)
      .slice(0, 10);
    
    // Top colecciones por calificación
    const topColeccionesCalificacion = colecciones
      .filter(c => c.tomos.some(t => t.calificacion && t.calificacion > 0))
      .map(c => {
        const calificaciones = c.tomos
          .filter(t => t.calificacion && t.calificacion > 0)
          .map(t => t.calificacion!);
        const promedio = calificaciones.reduce((sum, cal) => sum + cal, 0) / calificaciones.length;
        return {
          nombre: c.titulo,
          calificacion: promedio,
          tomosCalificados: calificaciones.length,
          totalTomos: c.tomos.length
        };
      })
      .sort((a, b) => b.calificacion - a.calificacion)
      .slice(0, 10);
    
    // Estadísticas de velocidad de lectura
    const velocidadLectura = colecciones
      .filter(c => c.tomos.some(t => t.fechaLecturaReciente && t.fechaCompra))
      .map(c => {
        const tomosConTiempo = c.tomos.filter(t => t.fechaLecturaReciente && t.fechaCompra);
        const tiempoPromedio = tomosConTiempo.reduce((sum, t) => {
          const dias = (t.fechaLecturaReciente! - t.fechaCompra!) / (1000 * 60 * 60 * 24);
          return sum + Math.max(1, dias);
        }, 0) / tomosConTiempo.length;
        
        return {
          nombre: c.titulo,
          tiempoPromedio: Math.round(tiempoPromedio),
          tomos: tomosConTiempo.length
        };
      })
      .sort((a, b) => a.tiempoPromedio - b.tiempoPromedio)
      .slice(0, 10);
    
    return {
      // Básicas
      totalColecciones,
      coleccionesCompletadas,
      coleccionesActivas,
      totalTomos,
      tomosLeidos,
      tomosComprados,
      tomosTBR,
      tomosLeyendo,
      tomosAbandonados,
      tomosWishlist,
      valorTotal,
      
      // Por categorías
      generos,
      editoriales,
      tiposTomo,
      idiomas,
      
      // Progreso
      progresoPorColeccion,
      comprasPorMes,
      lecturasPorMes,
      
      // Rankings
      topColeccionesValor,
      topColeccionesCalificacion,
      velocidadLectura
    };
  }, [state.coleccionesManga]);

  // Colores para gráficos
  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#F97316', '#EC4899', '#84CC16', '#6366F1'];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center space-x-2 mb-4"
        >
          <BookOpen className="h-8 w-8 text-indigo-500" />
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
            Estadísticas Avanzadas de Manga
          </h1>
        </motion.div>
        <p className="text-slate-600 dark:text-slate-400">
          Análisis detallado de tu colección de manga con gráficos interactivos
        </p>
      </div>

      {/* Tarjetas de resumen */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {[
          { title: 'Colecciones', value: statistics.totalColecciones, icon: BookOpen, color: 'bg-blue-500' },
          { title: 'Completadas', value: statistics.coleccionesCompletadas, icon: CheckCircle, color: 'bg-green-500' },
          { title: 'Activas', value: statistics.coleccionesActivas, icon: Clock, color: 'bg-yellow-500' },
          { title: 'Total Tomos', value: statistics.totalTomos, icon: BookMarked, color: 'bg-purple-500' },
          { title: 'Tomos Leídos', value: statistics.tomosLeidos, icon: CheckCircle, color: 'bg-teal-500' },
          { title: 'Valor Total', value: `${statistics.valorTotal.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}`, icon: DollarSign, color: 'bg-emerald-500' }
        ].map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="glass-effect rounded-xl p-4 text-center"
          >
            <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg ${stat.color} mb-3`}>
              <stat.icon className="h-6 w-6 text-white" />
            </div>
            <div className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
              {stat.value}
            </div>
            <div className="text-sm font-medium text-slate-700 dark:text-slate-300">
              {stat.title}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Gráfico de estado de tomos */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="glass-effect rounded-xl p-6"
      >
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 flex items-center">
          <BarChart3 className="h-5 w-5 mr-2 text-blue-500" />
          Estado de Tomos
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={[
            { estado: 'Leídos', cantidad: statistics.tomosLeidos, color: '#10B981' },
            { estado: 'Comprados', cantidad: statistics.tomosComprados, color: '#3B82F6' },
            { estado: 'TBR', cantidad: statistics.tomosTBR, color: '#F59E0B' },
            { estado: 'Leyendo', cantidad: statistics.tomosLeyendo, color: '#8B5CF6' },
            { estado: 'Abandonados', cantidad: statistics.tomosAbandonados, color: '#EF4444' },
            { estado: 'Wishlist', cantidad: statistics.tomosWishlist, color: '#EC4899' }
          ]}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="estado" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="cantidad" fill="#3B82F6" />
          </BarChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Gráficos de distribución */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Distribución por género */}
        {Object.keys(statistics.generos).length > 0 && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="glass-effect rounded-xl p-6"
          >
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 flex items-center">
              <Target className="h-5 w-5 mr-2 text-green-500" />
              Distribución por Género
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={Object.entries(statistics.generos).map(([genero, stats], index) => ({
                    name: genero,
                    value: stats.total,
                    color: colors[index % colors.length]
                  }))}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {Object.entries(statistics.generos).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </motion.div>
        )}

        {/* Distribución por editorial */}
        {Object.keys(statistics.editoriales).length > 0 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="glass-effect rounded-xl p-6"
          >
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 flex items-center">
              <BookOpen className="h-5 w-5 mr-2 text-purple-500" />
              Distribución por Editorial
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={Object.entries(statistics.editoriales).map(([editorial, stats], index) => ({
                    name: editorial,
                    value: stats.total,
                    color: colors[index % colors.length]
                  }))}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {Object.entries(statistics.editoriales).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </motion.div>
        )}
      </div>

      {/* Gráfico de progreso por colección */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="glass-effect rounded-xl p-6"
      >
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 flex items-center">
          <TrendingUp className="h-5 w-5 mr-2 text-blue-500" />
          Progreso de Lectura por Colección
        </h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={statistics.progresoPorColeccion.slice(0, 15)}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="nombre" angle={-45} textAnchor="end" height={100} />
            <YAxis />
            <Tooltip formatter={(value) => [`${typeof value === 'number' ? value.toFixed(1) : value}%`, 'Progreso']} />
            <Bar dataKey="progreso" fill="#3B82F6" />
          </BarChart>
        </ResponsiveContainer>
      </motion.div>



      {/* Rankings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top colecciones por valor */}
        {statistics.topColeccionesValor.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="glass-effect rounded-xl p-6"
          >
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 flex items-center">
              <DollarSign className="h-5 w-5 mr-2 text-emerald-500" />
              Top Colecciones por Valor
            </h3>
            <div className="space-y-3">
              {statistics.topColeccionesValor.map((coleccion, index) => (
                <div key={coleccion.nombre} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <span className="font-medium text-slate-900 dark:text-white">
                        {coleccion.nombre}
                      </span>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {coleccion.tomos} tomos
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-emerald-600 dark:text-emerald-400">
                      {coleccion.valor.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                    </div>
                    {coleccion.completada && (
                      <div className="text-xs text-green-600 dark:text-green-400">✓ Completada</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Top colecciones por calificación */}
        {statistics.topColeccionesCalificacion.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
            className="glass-effect rounded-xl p-6"
          >
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 flex items-center">
              <Star className="h-5 w-5 mr-2 text-yellow-500" />
              Top Colecciones por Calificación
            </h3>
            <div className="space-y-3">
              {statistics.topColeccionesCalificacion.map((coleccion, index) => (
                <div key={coleccion.nombre} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-yellow-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <span className="font-medium text-slate-900 dark:text-white">
                        {coleccion.nombre}
                      </span>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {coleccion.tomosCalificados}/{coleccion.totalTomos} tomos calificados
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-yellow-600 dark:text-yellow-400">
                      {coleccion.calificacion.toFixed(1)} ⭐
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>



      {/* Estadísticas de velocidad de lectura */}
      {statistics.velocidadLectura.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="glass-effect rounded-xl p-6"
        >
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 flex items-center">
            <Clock className="h-5 w-5 mr-2 text-blue-500" />
            Velocidad de Lectura por Colección
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={statistics.velocidadLectura}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="nombre" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip formatter={(value) => [`${typeof value === 'number' ? value : value} días`, 'Tiempo promedio']} />
              <Bar dataKey="tiempoPromedio" fill="#06B6D4" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      )}
    </div>
  );
};

export default MangaStatistics;